import React, { useMemo } from "react";
import {
  FaCube,
  FaUser,
  FaClock,
  FaBuilding,
  FaTruck,
  FaHashtag,
  FaLiraSign,
} from "react-icons/fa";
import FormatDateHourString from "../../Utils/FormatDateHourString";

/**
 * Fatura detayının gövdesi — firma Faturalar sekmesinin çekmecesi
 * (BillDetailDrawer) ve Uyarılar'daki son sipariş paneli (#367) ortak
 * kullanır. Kabuk (başlık, kapatma, kayma) çağırandadır.
 *
 * `bill` tek kalem ya da `lines` taşıyan GRUP olabilir (aynı fatura no'nun
 * kalemleri).
 */
const InfoCard = ({ icon: Icon, label, children, className = "" }) => (
  <div className={`bg-white border border-gray-100 rounded-xl px-4 py-3 ${className}`}>
    <p className="flex items-center gap-1.5 text-xs text-gray-400 font-semibold tracking-wider mb-1">
      {Icon && <Icon className="w-3 h-3" />}
      {label}
    </p>
    {children}
  </div>
);

const BillDetailContent = ({ bill, typeDetails = null, note = "" }) => {
  const isBill = bill?.billType === "Bill";

  // GRUP DESTEĞİ (2026-08-25): storage Faturalar sekmesi aynı faturanın
  // kalemlerini tek satırda topluyor ve buraya `lines` taşıyan bir GRUP
  // gönderiyor. Bayi sekmesi hâlâ TEK kalem gönderiyor — bu yüzden `lines`
  // yoksa faturanın kendisi tek elemanlı liste sayılır.
  // useMemo: aksi halde her render'da yeni dizi üretilir ve aşağıdaki
  // productSummary useMemo'su hiçbir zaman cache'lenmez.
  const lines = useMemo(
    () =>
      Array.isArray(bill?.lines) && bill.lines.length > 0
        ? bill.lines
        : bill
          ? [bill]
          : [],
    [bill],
  );
  const isGroup = lines.length > 1;

  // Ürün tipi bazında toplamlar — listedeki "Türk Kahvesi × 2000" ile birebir
  // aynı hesap; grup gelmediyse tek kalemden üretilir.
  const productSummary = useMemo(() => {
    if (Array.isArray(bill?.productSummary)) return bill.productSummary;
    const m = new Map();
    lines.forEach((l) => {
      const name = l.productTypeName || l.productName || "-";
      m.set(name, (m.get(name) || 0) + (Number(l.quota) || 0));
    });
    return Array.from(m.entries()).map(([name, quota]) => ({ name, quota }));
  }, [bill, lines]);

  if (!bill) return null;

  // TUTAR = income (SATIR TOPLAMI). `price` BİRİM FİYAT'tır
  // (BillPage.js: price = SATIR TOPLAMI / SATIR MİKTARI) ve TOPLANAMAZ.
  // Birim fiyat tek kalemde ya da tüm kalemlerin fiyatı aynıysa gösterilir;
  // farklıysa KALEMLER listesinde kalem kalem.
  const totalIncome =
    bill.totalIncome ?? lines.reduce((a, l) => a + (Number(l.income) || 0), 0);
  const prices = [...new Set(lines.map((l) => Number(l.price) || 0))];
  const unitPrice = prices.length === 1 ? prices[0] : null;

  return (
    <>
      {/* İşlem türü + tarih */}
      <div className="flex items-center justify-between gap-2">
        {typeDetails ? (
          <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-bold border ${typeDetails.color}`}>
            {typeDetails.title}
          </span>
        ) : (
          <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-bold border bg-gray-50 text-gray-600 border-gray-200">
            {bill.billType}
          </span>
        )}
        <span className="inline-flex items-center gap-1.5 text-sm text-gray-500">
          <FaClock className="w-3.5 h-3.5" />
          {FormatDateHourString(bill.createdInfo)}
        </span>
      </div>

      {/* KOTA PAYLAŞIMI — karşı taraf. Bu kayıt tek başına yarım bir
          hareket: kotanın nereden geldiği / nereye gittiği yalnızca
          burada görünür (bkz. subfirm-quota-transfer.service). */}
      {bill.billType === "QuotaShare" && bill.quotaShare && (
        <InfoCard
          icon={FaBuilding}
          label={
            bill.quotaShare.direction === "out"
              ? "KOTA GÖNDERİLEN FİRMA"
              : "KOTAYI GÖNDEREN FİRMA"
          }
        >
          <p className="text-base font-bold text-gray-800 truncate">
            {bill.quotaShare.counterpartFirmName || "-"}
          </p>
          <p className="text-xs text-gray-400">
            {bill.quotaShare.direction === "out"
              ? "Bu kota firmanın havuzundan düşüldü."
              : "Bu kota firmanın havuzuna eklendi."}
          </p>
        </InfoCard>
      )}

      {/* Ürün & Miktar — ürün TİPİ bazında toplam */}
      <InfoCard icon={FaCube} label={isGroup ? "ÜRÜN TOPLAMLARI" : "ÜRÜN"}>
        {productSummary.map((p) => (
          <div key={p.name} className="flex items-baseline justify-between gap-2 py-0.5">
            <p className="text-base font-bold text-gray-800 truncate">{p.name}</p>
            <p className="text-base font-Bold text-indigo-600 tabular-nums shrink-0">
              × {p.quota.toLocaleString("tr-TR")}
            </p>
          </div>
        ))}
        {!isGroup && (
          <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5">
            {bill.productName && bill.productName !== (bill.productTypeName || bill.productName) && (
              <p className="text-sm text-gray-500">
                Ürün: <span className="font-medium text-gray-700">{bill.productName}</span>
              </p>
            )}
            {bill.productCode && (
              <p className="text-sm text-gray-500">
                Kod: <span className="font-mono font-medium text-gray-700">{bill.productCode}</span>
              </p>
            )}
          </div>
        )}
      </InfoCard>

      {/* KALEMLER — grubun altındaki tek tek fatura satırları.
          Toplamın nereden geldiği burada görünür. */}
      {isGroup && (
        <InfoCard icon={FaCube} label={`KALEMLER (${lines.length})`}>
          <div className="divide-y divide-gray-100 -my-1">
            {lines.map((l, i) => (
              <div key={l._id || i} className="py-2 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-800 truncate">
                    {l.productName || "-"}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    {l.productTypeName || "-"}
                    {l.productCode ? ` · ${l.productCode}` : ""}
                    {` · ${FormatDateHourString(l.createdInfo)}`}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-Bold text-indigo-600 tabular-nums">
                    × {(Number(l.quota) || 0).toLocaleString("tr-TR")}
                  </p>
                  {isBill && l.income ? (
                    <p className="text-xs text-gray-600 tabular-nums font-SemiBold">
                      ₺{Number(l.income).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                    </p>
                  ) : null}
                  {isBill && l.price ? (
                    <p className="text-[11px] text-gray-400 tabular-nums">
                      birim ₺{Number(l.price).toLocaleString("tr-TR", { minimumFractionDigits: 2 })}
                    </p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </InfoCard>
      )}

      {/* Tutar — sadece gerçek faturada */}
      {isBill && (
        <div className="grid grid-cols-2 gap-3">
          <InfoCard icon={FaLiraSign} label={isGroup ? "TOPLAM TUTAR" : "TUTAR"}>
            <p className="text-lg font-Bold text-emerald-600 tabular-nums">
              {totalIncome
                ? `₺${totalIncome.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`
                : "-"}
            </p>
          </InfoCard>
          <InfoCard icon={FaLiraSign} label="BİRİM FİYAT">
            <p className="text-lg font-Bold text-gray-800 tabular-nums">
              {unitPrice
                ? `₺${unitPrice.toLocaleString("tr-TR", { minimumFractionDigits: 2 })}`
                : "—"}
            </p>
            {isGroup && unitPrice === null && (
              <p className="text-xs text-gray-400 mt-0.5">
                Kalem bazında — aşağıda
              </p>
            )}
          </InfoCard>
        </div>
      )}

      {/* Fatura kimliği */}
      <div className="grid grid-cols-2 gap-3">
        <InfoCard icon={FaHashtag} label="FATURA NO">
          <p className="text-sm font-semibold text-gray-800 font-mono break-all">{bill.billNo || "-"}</p>
        </InfoCard>
        <InfoCard icon={FaUser} label="İŞLEMİ YAPAN">
          <p className="text-sm font-semibold text-gray-800 truncate">{bill.billerName || "-"}</p>
        </InfoCard>
      </div>

      {/* Firma ünvanı */}
      {bill.firmApellation && (
        <InfoCard icon={FaBuilding} label="FİRMA ÜNVANI">
          <p className="text-sm font-semibold text-gray-800">{bill.firmApellation}</p>
        </InfoCard>
      )}

      {/* Not / Sevkiyat bilgisi */}
      {(note || bill.shippingInfo) && (
        <InfoCard icon={FaTruck} label={note ? "NOT" : "SEVKİYAT BİLGİSİ"} className="bg-amber-50/60 border-amber-100">
          <p className="text-sm text-gray-700 leading-relaxed">{note || bill.shippingInfo}</p>
        </InfoCard>
      )}
    </>
  );
};

export default BillDetailContent;
